import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Download, Share, PlusSquare, MoreVertical } from "lucide-react";
import { useTranslation } from "react-i18next";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () => {
  const ua = window.navigator.userAgent;
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  return iOSDevice || iPadOS;
};

export const InstallPrompt = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    if (window.deferredInstallPrompt) setDeferred(window.deferredInstallPrompt);
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstallable = () => { if (window.deferredInstallPrompt) setDeferred(window.deferredInstallPrompt); };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("bdc-installable", onInstallable);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("bdc-installable", onInstallable);
    };
  }, []);

  useEffect(() => {
    if (!user || isStandalone()) return;
    // Ne pas interrompre le parcours ambassadeur (offre en cours d'utilisation)
    if (location.pathname.startsWith("/ambassadeur")) return;
    if ((user.free_eternal_candles || 0) > 0) return;
    const key = `bdc_install_seen_${user.id}`;
    if (localStorage.getItem(key)) return;
    const timer = setTimeout(() => {
      localStorage.setItem(key, "1");
      setOpen(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [user, location.pathname]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) {}
    setDeferred(null);
    setOpen(false);
  };

  const ios = isIOS();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="glass border-white/10 sm:max-w-sm" data-testid="install-prompt-dialog">
        <DialogHeader className="items-center text-center">
          <span className="mb-2 select-none font-semibold tracking-tight text-3xl" data-testid="install-prompt-logo">
            <span className="text-white">{t("brand.part1")}</span><span className="text-sky-400">{t("brand.part2")}</span>
          </span>
          <DialogTitle className="text-xl font-medium">{t("install.title")}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">{t("install.desc")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          {deferred ? (
            <Button className="w-full rounded-full" onClick={install} data-testid="install-prompt-btn">
              <Download size={16} className="mr-2" /> {t("install.button")}
            </Button>
          ) : ios ? (
            <div className="space-y-2 rounded-2xl border border-white/10 bg-card/40 p-4 text-sm text-foreground/90" data-testid="install-ios-steps">
              <p className="flex items-center gap-2"><Share size={16} className="text-sky-400" /> {t("install.iosStep1")}</p>
              <p className="flex items-center gap-2"><PlusSquare size={16} className="text-sky-400" /> {t("install.iosStep2")}</p>
            </div>
          ) : (
            <div className="space-y-2 rounded-2xl border border-white/10 bg-card/40 p-4 text-sm text-foreground/90" data-testid="install-android-steps">
              <p className="flex items-center gap-2"><MoreVertical size={16} className="text-sky-400" /> {t("install.androidStep1")}</p>
              <p className="flex items-center gap-2"><PlusSquare size={16} className="text-sky-400" /> {t("install.androidStep2")}</p>
            </div>
          )}
          <Button variant="ghost" className="w-full rounded-full text-muted-foreground" onClick={() => setOpen(false)} data-testid="install-later-btn">
            {t("install.later")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
